import { useState, useEffect } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { Film, Tv, Sparkles, Plus, Filter, SlidersHorizontal, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import EnhancedRecommendationCard from './EnhancedRecommendationCard';
import EnhancedRecommendationModal from './EnhancedRecommendationModal';
import IdeaForm from './IdeaForm';
import AIRecommendationsModal from './AIRecommendationsModal';
import QuickAddSeenModal from './QuickAddSeenModal';
import QuickRatingInterface from './QuickRatingInterface';
import WatchPreferencesModal from './WatchPreferencesModal';

export default function WatchLibrary() {
  const { currentUser, recsIdeas, deleteIdea } = useFamilyContext();
  const [watchedMovies, setWatchedMovies] = useState(new Map());
  const [loadingWatchHistory, setLoadingWatchHistory] = useState(false);
  const [showAIRecommendations, setShowAIRecommendations] = useState(false);
  const [showQuickAddSeen, setShowQuickAddSeen] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deletingIdea, setDeletingIdea] = useState(false);
  const [selectedIdeaDetails, setSelectedIdeaDetails] = useState(null);
  const [editingIdea, setEditingIdea] = useState(null);

  // Filters
  const [watchFilter, setWatchFilter] = useState('to-watch'); // 'to-watch' | 'watched' | 'all'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'movie' | 'tv'
  const [genreFilter, setGenreFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all'); // 'all' | 'loved' | 'liked' | etc.
  const [sortBy, setSortBy] = useState('added'); // 'added' | 'year' | 'rating' | 'title'
  const [showFilters, setShowFilters] = useState(false);

  const isActualParent = currentUser?.isParent && !currentUser?.originalParent;

  // Get all movies/TV shows
  const allWatchContent = (recsIdeas || []).filter(idea => idea.category === 'movie');

  // Extract unique values for filters
  const genres = [...new Set(
    allWatchContent
      .map(item => item.genre?.split(',').map(g => g.trim()))
      .flat()
      .filter(Boolean)
  )].sort();

  const years = [...new Set(
    allWatchContent
      .map(item => item.release_year)
      .filter(Boolean)
  )].sort((a, b) => b - a);

  // Fetch watch history
  const fetchWatchHistory = async () => {
    if (!currentUser?.id) return;

    setLoadingWatchHistory(true);
    try {
      const response = await fetch('/api/watch', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get-watch-history',
          userId: currentUser.id
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API error (${response.status}): ${errorText}`);
      }

      const data = await response.json();
      if (data.success && data.history) {
        const watchMap = new Map();
        data.history.forEach(entry => {
          if (entry.entertainment_id) {
            watchMap.set(entry.entertainment_id, {
              rating: entry.watch_rating,
              watchedDate: entry.watched_date,
              status: entry.watch_status,
              notes: entry.notes
            });
          }
        });
        setWatchedMovies(watchMap);
      }
    } catch (error) {
      console.error('Error fetching watch history:', error);
      toast.error('Failed to load watch history');
    } finally {
      setLoadingWatchHistory(false);
    }
  };

  useEffect(() => {
    fetchWatchHistory();
  }, [currentUser?.id]);

  // Handle watch rating updates
  const handleWatchUpdate = async (historyEntry, movieId) => {
    if (historyEntry === null && movieId) {
      setWatchedMovies(prev => {
        const newMap = new Map(prev);
        newMap.delete(movieId);
        return newMap;
      });
      return;
    }

    if (!historyEntry || !historyEntry.entertainment_id) return;

    setWatchedMovies(prev => {
      const newMap = new Map(prev);
      newMap.set(historyEntry.entertainment_id, {
        rating: historyEntry.watch_rating,
        watchedDate: historyEntry.watched_date,
        status: historyEntry.watch_status,
        notes: historyEntry.notes
      });
      return newMap;
    });

    const ratingLabels = {
      loved: 'Loved ',
      liked: 'Liked ',
      neutral: 'Meh ',
      hated: 'Disliked ',
      disliked: 'Disliked '
    };
    toast.success(`Marked as ${ratingLabels[historyEntry.watch_rating] || 'Seen'}`);
  };

  // Handle deleting idea
  const handleDeleteIdea = async (ideaId) => {
    try {
      await deleteIdea(ideaId);

      setWatchedMovies(prev => {
        const newMap = new Map(prev);
        newMap.delete(ideaId);
        return newMap;
      });

      toast.success('Deleted from library and watch history');
    } catch (error) {
      console.error('Error deleting idea:', error);
      toast.error('Failed to delete');
    }
  };

  // Apply filters
  const filteredContent = allWatchContent.filter(item => {
    // Watch status filter
    const isWatched = watchedMovies.has(item.id);
    if (watchFilter === 'to-watch' && isWatched) return false;
    if (watchFilter === 'watched' && !isWatched) return false;

    // Type filter (movie vs TV)
    if (typeFilter === 'movie' && item.is_tv) return false;
    if (typeFilter === 'tv' && !item.is_tv) return false;

    // Genre filter
    if (genreFilter !== 'all') {
      const itemGenres = item.genre?.toLowerCase().split(',').map(g => g.trim()) || [];
      if (!itemGenres.includes(genreFilter.toLowerCase())) return false;
    }

    // Year filter
    if (yearFilter !== 'all' && item.release_year != yearFilter) return false;

    // Rating filter
    if (ratingFilter !== 'all') {
      const watchData = watchedMovies.get(item.id);
      if (!watchData || watchData.rating !== ratingFilter) return false;
    }

    return true;
  });

  // Apply sorting
  const sortedContent = [...filteredContent].sort((a, b) => {
    switch (sortBy) {
      case 'year':
        return (b.release_year || 0) - (a.release_year || 0);
      case 'rating':
        const ratingOrder = { loved: 5, liked: 4, neutral: 3, disliked: 2, hated: 1 };
        const aRating = ratingOrder[watchedMovies.get(a.id)?.rating] || 0;
        const bRating = ratingOrder[watchedMovies.get(b.id)?.rating] || 0;
        return bRating - aRating;
      case 'title':
        return (a.title || '').localeCompare(b.title || '');
      case 'added':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header - Rotten Tomatoes inspired with coral */}
      <div className="bg-coral-600 text-white shadow-lg border-b-2 border-slate-900">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold mb-2">Watch Library</h1>
              <p className="text-white text-opacity-95">
                Track movies and TV shows you want to watch or have watched
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowQuickAddSeen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-medium transition-colors backdrop-blur-sm"
              >
                <Plus className="w-5 h-5" />
                Build History
              </button>
              <button
                onClick={() => setShowPreferences(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg font-medium transition-colors backdrop-blur-sm"
              >
                <SlidersHorizontal className="w-5 h-5" />
                Preferences
              </button>
              <button
                onClick={() => setShowAIRecommendations(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white text-[#FF6B6B] hover:bg-opacity-90 rounded-lg font-medium transition-colors shadow-md"
              >
                <Sparkles className="w-5 h-5" />
                AI Recommendations
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mt-6">
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20">
              <div className="text-xl font-bold">{allWatchContent.length}</div>
              <div className="text-sm text-white text-opacity-95">Total</div>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20">
              <div className="text-xl font-bold">{watchedMovies.size}</div>
              <div className="text-sm text-white text-opacity-95">Watched</div>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20">
              <div className="text-xl font-bold">{allWatchContent.length - watchedMovies.size}</div>
              <div className="text-sm text-white text-opacity-95">To Watch</div>
            </div>
            <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20">
              <div className="text-xl font-bold">
                {Array.from(watchedMovies.values()).filter(w => w.rating === 'loved').length}
              </div>
              <div className="text-sm text-white text-opacity-95">Loved ❤️</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-8 py-4">
          {/* Main filter tabs */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex gap-2">
              <button
                onClick={() => setWatchFilter('to-watch')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  watchFilter === 'to-watch'
                    ? 'bg-[#FF6B6B] text-white shadow-lg'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                To Watch ({allWatchContent.length - watchedMovies.size})
              </button>
              <button
                onClick={() => setWatchFilter('watched')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  watchFilter === 'watched'
                    ? 'bg-[#FF8E53] text-white shadow-lg'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                Watched ({watchedMovies.size})
              </button>
              <button
                onClick={() => setWatchFilter('all')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  watchFilter === 'all'
                    ? 'bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white shadow-lg'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                All ({allWatchContent.length})
              </button>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 rounded-lg font-medium transition-colors"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>

          {/* Advanced filters */}
          {showFilters && (
            <div className="grid grid-cols-5 gap-3 pb-4 border-t border-stone-200 pt-4">
              {/* Type filter */}
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6B6B] focus:border-[#FF6B6B]"
              >
                <option value="all">All Types</option>
                <option value="movie">Movies</option>
                <option value="tv">TV Shows</option>
              </select>

              {/* Genre filter */}
              <select
                value={genreFilter}
                onChange={(e) => setGenreFilter(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6B6B] focus:border-[#FF6B6B]"
              >
                <option value="all">All Genres</option>
                {genres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>

              {/* Year filter */}
              <select
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6B6B] focus:border-[#FF6B6B]"
              >
                <option value="all">All Years</option>
                {years.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>

              {/* Rating filter */}
              <select
                value={ratingFilter}
                onChange={(e) => setRatingFilter(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6B6B] focus:border-[#FF6B6B]"
              >
                <option value="all">All Ratings</option>
                <option value="loved">Loved</option>
                <option value="liked">Liked</option>
                <option value="neutral">Meh</option>
                <option value="disliked">Disliked</option>
              </select>

              {/* Sort by */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-3 py-2 border border-stone-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6B6B] focus:border-[#FF6B6B]"
              >
                <option value="added">Recently Added</option>
                <option value="year">Year</option>
                <option value="rating">Rating</option>
                <option value="title">Title (A-Z)</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Content Grid */}
      <div className="max-w-7xl mx-auto px-8 py-8">
        {loadingWatchHistory ? (
          <div className="text-center py-12">
            <div className="text-stone-600">Loading watch history...</div>
          </div>
        ) : sortedContent.length === 0 ? (
          <div className="text-center py-12">
            <Film className="w-16 h-16 text-stone-300 mx-auto mb-4" />
            <h3 className="text-md font-semibold text-stone-900 mb-2">No movies found</h3>
            <p className="text-stone-600">Try adjusting your filters or add some movies!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {sortedContent.map(item => (
              <EnhancedRecommendationCard
                key={item.id}
                recommendation={item}
                onViewDetails={(idea) => {
                  console.log('View Details clicked:', idea);
                  setSelectedIdeaDetails(idea);
                }}
                onEdit={(idea) => {
                  console.log('Edit clicked:', idea);
                  setEditingIdea(idea);
                }}
                onDelete={handleDeleteIdea}
                onConfirmDelete={(id) => {
                  console.log('Delete clicked from card:', id, 'Type:', typeof id);
                  setDeleteConfirmId(id);
                }}
                currentUser={currentUser}
                isParent={isActualParent}
                onWatchUpdate={handleWatchUpdate}
                watchedMovies={watchedMovies}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAIRecommendations && (
        <AIRecommendationsModal
          userId={currentUser?.id}
          authUserId={currentUser?.authUserId}
          onClose={() => setShowAIRecommendations(false)}
          onMovieAdded={() => {
            fetchWatchHistory();
          }}
        />
      )}

      {showQuickAddSeen && (
        <QuickAddSeenModal
          userId={currentUser?.id}
          authUserId={currentUser?.authUserId}
          onClose={() => setShowQuickAddSeen(false)}
          onAdd={() => {
            fetchWatchHistory();
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-stone-900 mb-2">Delete Movie?</h3>
            <p className="text-stone-600 mb-6">
              This will remove it from your library and watch history. This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                disabled={deletingIdea}
                className="flex-1 px-4 py-3 border border-stone-300 text-stone-600 font-medium rounded-lg hover:bg-stone-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeletingIdea(true);
                  try {
                    // Ensure we have a valid ID string
                    const idToDelete = typeof deleteConfirmId === 'object' ? deleteConfirmId?.id : deleteConfirmId;
                    console.log('Deleting with ID:', idToDelete, 'Type:', typeof idToDelete);
                    await handleDeleteIdea(idToDelete);
                    setDeleteConfirmId(null);
                  } catch (error) {
                    // Error already handled in handleDeleteIdea
                  } finally {
                    setDeletingIdea(false);
                  }
                }}
                disabled={deletingIdea}
                className="flex-1 px-4 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deletingIdea ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {selectedIdeaDetails && (
        <EnhancedRecommendationModal
          recommendation={selectedIdeaDetails}
          onClose={() => setSelectedIdeaDetails(null)}
          onEdit={(idea) => {
            setEditingIdea(idea);
            setSelectedIdeaDetails(null);
          }}
          onDelete={(ideaId) => {
            console.log('Delete clicked from modal:', ideaId, 'Type:', typeof ideaId);
            setDeleteConfirmId(ideaId);
            setSelectedIdeaDetails(null);
          }}
          isParent={isActualParent}
          currentUser={currentUser}
          onWatchUpdate={handleWatchUpdate}
          watchedMovies={watchedMovies}
        />
      )}

      {/* Edit Modal */}
      {editingIdea && (
        <IdeaForm
          idea={editingIdea}
          onClose={() => setEditingIdea(null)}
          onSave={() => {
            setEditingIdea(null);
            fetchWatchHistory();
          }}
        />
      )}

      {/* Watch Preferences Modal */}
      {showPreferences && (
        <WatchPreferencesModal
          userId={currentUser?.id}
          onClose={() => setShowPreferences(false)}
          onSave={() => {
            setShowPreferences(false);
            toast.success('Preferences saved!');
          }}
        />
      )}
    </div>
  );
}
